// Tiny slice store + async-flight guards.
//
// Replaces the legacy globals (cache.{inbox,intake,safety,patients}, CU, CPD,
// USERS, …) with one store of named slices. Views subscribe to the slices they
// render; writers call set(slice, patch). No proxies, no batching — a set()
// synchronously notifies that slice's subscribers only.

// Slice-name constants — always reference these, never bare strings.
export const SLICES = Object.freeze({
  SESSION: 'session', // {user, role, fullName} (legacy CU / CUR / CU.full_name)
  USERS: 'users', // user_id -> {full_name, role} (legacy USERS)
  PATIENTS: 'patients', // complete cases set (legacy cache.patients)
  INBOX: 'inbox', // today queue rows (legacy cache.inbox)
  INTAKE: 'intake', // consent submissions (legacy cache.intake)
  SAFETY: 'safety', // AE register rows (legacy cache.safety)
  PROGRAMMES: 'programmes', // programme registry + active filter
  UI: 'ui', // {mode:'simple'|'full', view, …}
  COUNTS: 'counts', // sidebar badge numbers keyed by route id (legacy cnt-t/i/p/s)
  FILTERS: 'filters', // app-wide data filters: {programme: 'all'|code} (legacy activeProgramme)
  REALTIME: 'realtime', // {status:'connecting'|'live'|'offline'} — transport state only (legacy Live badge)
  RT_CHANGES: 'rtChanges', // {changed:[keys], at} — coalesced data-change notifications (own slice so status flaps never re-deliver a stale batch)
  QUEUE: 'queue', // Today queue: {rows, all, refInfo, counts, loadError, ownerJoinFailed} (legacy cache.inbox/inboxAll/inboxRefInfo)
  REPORTS: 'reports', // Reports raw snapshot: {raw, loaded, loadError, truncated} — fetched ONCE per visit; every filter/period recomputes client-side from it (legacy rptRaw)
  WORKSPACE: 'workspace', // open patient overlay: {caseId, case, panels{tasks,contacts,aes,deliveries,cycles,stageHist,docs,assignHist}, loadError, notFound} (legacy CPD/CPT)
  TRAINING: 'training', // training course: {videos, questions, progress, answers, open, loadError}
});

/**
 * Create an isolated store instance (the app uses the default export; tests
 * create their own).
 * @param {Record<string, any>} [initial] initial slice values
 */
export function createStore(initial) {
  /** @type {Record<string, any>} */
  const slices = Object.assign({}, initial);
  /** @type {Record<string, Set<Function>>} */
  const subs = {};

  return {
    /**
     * Current value of a slice (undefined if never set).
     * @param {string} slice
     */
    get(slice) {
      return slices[slice];
    },
    /**
     * Update a slice and notify ITS subscribers only. A plain-object patch is
     * shallow-merged onto a plain-object current value; anything else
     * (arrays, primitives, null) REPLACES the slice.
     * @param {string} slice
     * @param {any} patch
     */
    set(slice, patch) {
      const cur = slices[slice];
      const isMerge =
        patch && typeof patch === 'object' && !Array.isArray(patch) &&
        cur && typeof cur === 'object' && !Array.isArray(cur);
      const next = isMerge ? Object.assign({}, cur, patch) : patch;
      slices[slice] = next;
      const set = subs[slice];
      if (set) for (const fn of [...set]) fn(next, slice);
    },
    /**
     * Subscribe to ONE slice. Returns an unsubscribe function.
     * @param {string} slice
     * @param {(value: any, slice: string) => void} fn
     * @returns {() => void}
     */
    subscribe(slice, fn) {
      (subs[slice] || (subs[slice] = new Set())).add(fn);
      return () => {
        const set = subs[slice];
        if (set) set.delete(fn);
      };
    },
  };
}

// The app-wide store singleton.
export const store = createStore();

// ─── flight(): single-flight + latest-wins async guards ────────────────────
// Ports BOTH legacy concurrency idioms:
//  - `share(fn)` = _casesInflight: concurrent callers of the same loader share
//    ONE in-progress promise (the second caller must not cancel the first and
//    leave its view on skeletons).
//  - `begin()` = wsEpoch / ptLoadEpoch: each call claims a new epoch and
//    returns an `isStale()` probe; a load checks it after every await and
//    abandons (never commits) once a newer load has begun.
export function flight() {
  /** @type {Promise<any>|null} */
  let inflight = null;
  let epoch = 0;

  return {
    /**
     * Single-flight: if a run is in progress, return ITS promise; otherwise
     * start fn(). The slot frees when the run settles (success or failure).
     * @template T
     * @param {() => Promise<T>|T} fn
     * @returns {Promise<T>}
     */
    share(fn) {
      if (inflight) return inflight;
      inflight = Promise.resolve()
        .then(fn)
        .finally(() => {
          inflight = null;
        });
      return inflight;
    },
    /**
     * Latest-wins: claim a new epoch (staling every earlier claim) and return
     * an isStale() probe for THIS claim. Call it after each await; when true,
     * bail without committing state.
     * @returns {() => boolean}
     */
    begin() {
      const e = ++epoch;
      return () => e !== epoch;
    },
  };
}
